/**
 * Category-related logic for FilterController (creation, sorting, selection mapping)
 */

// Categories now come directly from file properties (L, F, M, S) in files_blob
// No mapping needed - values are used as-is

// Create filters from categories
export function createFilters(ctx, categories) {
	ctx.filters = {};
	for (const [k, v] of Object.entries(categories)) {
		let cats = k.split("/");
		for (let c in cats) {
			let parts = /^([^:]+):([A-Z])(?:\[\d+\])?/.exec(cats[c]);
			if (!parts) continue;
			let cname = parts[1];
			let ctype = parts[2];
			let translation = cname;
			if (ctx.filters.hasOwnProperty(ctype)) {
				if ($.inArray(translation, ctx.filters[ctype]) == -1) {
					ctx.filters[ctype].push(translation);
				}
			} else {
				ctx.filters[ctype] = [];
				ctx.filters[ctype].push(translation);
			}
		}
	}
	// Filters created logging only in debug mode
}

// Sort filters according to predefined order
export function sortFilters(ctx, language) {
	if (!ctx.filterOrder['L'] || !Array.isArray(ctx.filterOrder['L'])) {
		console.error('[FilterController] filterOrder.L is not properly initialized:', ctx.filterOrder['L']);
		ctx.filterOrder['L'] = ['EN', 'RU', 'DE', 'FR', 'ES'];
	}
	
	// Sort languages: interface language first, then English, then others
	const interfaceLang = language ? language.toUpperCase() : 'EN';
	
	// Get actual languages from filters['L'] if available
	if (!ctx.filters || !ctx.filters['L'] || !Array.isArray(ctx.filters['L']) || ctx.filters['L'].length === 0) {
		return; // Nothing to sort
	}
	
	// Create a mapping of uppercase to original case for preserving original case
	const langCaseMap = {};
	ctx.filters['L'].forEach(lang => {
		const upperLang = String(lang).toUpperCase();
		if (!langCaseMap[upperLang]) {
			langCaseMap[upperLang] = lang; // Store first occurrence as reference
		}
	});
	
	// Get all unique languages in uppercase
	const actualLangsUpper = Array.from(new Set(ctx.filters['L'].map(l => String(l).toUpperCase())));
	
	// Get reference order in uppercase
	const referenceOrder = ctx.filterOrder['L'].map(l => String(l).toUpperCase());
	const sortedLangsUpper = [];
	
	// 1. Add interface language first (if it exists in actual filters)
	if (actualLangsUpper.includes(interfaceLang)) {
		sortedLangsUpper.push(interfaceLang);
		actualLangsUpper.splice(actualLangsUpper.indexOf(interfaceLang), 1);
	}
	
	// 2. Add English second (if not interface language and exists in filters)
	if (interfaceLang !== 'EN' && actualLangsUpper.includes('EN')) {
		sortedLangsUpper.push('EN');
		actualLangsUpper.splice(actualLangsUpper.indexOf('EN'), 1);
	}
	
	// 3. Add remaining languages in reference order
	for (const refLang of referenceOrder) {
		if (actualLangsUpper.includes(refLang)) {
			sortedLangsUpper.push(refLang);
			actualLangsUpper.splice(actualLangsUpper.indexOf(refLang), 1);
		}
	}
	
	// 4. Add any remaining languages that weren't in reference order
	sortedLangsUpper.push(...actualLangsUpper);
	
	// Update filterOrder['L'] with sorted order (using original case from map, or uppercase if not found)
	ctx.filterOrder['L'] = sortedLangsUpper.map(langUpper => langCaseMap[langUpper] || langUpper);
	
	// Sort filters array using the sorted order (preserving original case)
	for (const [k, v] of Object.entries(ctx.filters)) {
		if (k === 'mgGroups') continue; // Skip mgGroups metadata
		if (ctx.filterOrder.hasOwnProperty(k)) {
			if (window.sort_array_based_on_example) {
				// Use original case for comparison but sorted order
				window.sort_array_based_on_example(v, ctx.filterOrder[k]);
			}
		}
	}
	if (window.sort_array_keys_based_on_example) {
		ctx.filters = window.sort_array_keys_based_on_example(ctx.filters, ctx.filterOrder['filters']);
	}
}

// Get selected categories as file property filters (not CATEG IDs)
export function getSelectedCategories(ctx) {
	// Return selected values for each group directly
	const selections = {};
	for (const [k, tree] of Object.entries(ctx.trees || {})) {
		if (!tree) continue;
		const treeValue = tree.allValue || tree.value || [];
		if (Array.isArray(treeValue) && treeValue.length > 0) {
			// For M group with hierarchical structure (MG groups), expand MG selections to include all M children
			if (k === 'M' && ctx.filters && ctx.filters.mgGroups) {
				const expandedValues = [];
				for (const val of treeValue) {
					// Check if this value is an MG group
					if (ctx.filters.mgGroups[val]) {
						// Expand to include all M values in this MG group
						expandedValues.push(...ctx.filters.mgGroups[val]);
					} else {
						// Regular M value, keep as-is
						expandedValues.push(val);
					}
				}
				// Remove duplicates
				selections[k] = [...new Set(expandedValues)];
			} else {
				selections[k] = treeValue;
			}
		}
	}
	
	// Log only when there are selections and in debug mode
	if (Object.keys(selections).length > 0 && window.HyperLog && debugMode === '1') {
		window.HyperLog.debug('[FilterController] getSelectedCategories', {
			selections,
			totalGroups: Object.keys(selections).length,
			selectionsDetails: JSON.stringify(selections),
			mSelection: selections.M || []
		});
	}
	
	// Return selections as-is for direct property filtering
	// We no longer return CATEG IDs
	return selections;
}


// Compute availability of category options per group based on current selection
export function computeAvailability(ctx) {
    try {
        const files = (window.filesStore && typeof window.filesStore.getFiles === 'function') ? window.filesStore.getFiles() : [];
        
        // Current selections by group
        const selByGroup = ctx.activeFilters && ctx.activeFilters.categories ? ctx.activeFilters.categories : {};
        const groups = Object.keys(ctx.trees || {});
        const availability = {};
        

        for (const g of groups) {
            const tree = ctx.trees[g]; if (!tree) continue;
            
            // Step 1: Filter files by selections in OTHER groups (exclude current group g)
            let filteredFiles = files;
            for (const h of groups) {
                if (h === g) continue;
                const vals = Array.isArray(selByGroup[h]) ? selByGroup[h] : [];
                if (vals.length === 0) continue;
                
                filteredFiles = filteredFiles.filter(file => {
                    const fileVal = file[h] || 'empty';
                    return vals.includes(fileVal);
                });
            }
            
            // Step 2: For each option in current group, check if any filtered files match
            const enabled = new Set();
            const options = tree.options || [];
            for (const opt of options) {
                const val = opt && opt.value;
                if (!val) {
                    // Skip options without value (these are MG parent groups with isGroupSelectable: false)
                    continue;
                }
                
                // For M group with hierarchical structure, check both MG parent and M children
                if (g === 'M' && opt.children && opt.children.length > 0) {
                    // This is an MG parent - check all its M children
                    for (const child of opt.children) {
                        const childVal = child && child.value;
                        if (!childVal) continue;
                        const hasMatch = filteredFiles.some(file => {
                            const fileVal = file[g] || 'empty';
                            return fileVal === childVal;
                        });
                        if (hasMatch) enabled.add(childVal);
                    }
                } else {
                    // Regular option - check the value directly
                    const hasMatch = filteredFiles.some(file => {
                        const fileVal = file[g] || 'empty';
                        return fileVal === val;
                    });
                    if (hasMatch) enabled.add(val);
                }
            }
            
            // Group availability logging removed to reduce noise
            availability[g] = enabled;
        }
        return availability;
    } catch (e) {
        if (debugMode === '1') { console.error('[FilterController] computeAvailability error', e); }
        return {};
    }
}


